import React from 'react';
import Navigation from '../../components/layout/Navigation';

const CookiePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation isAuthenticated={false} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* IMPORTANT NOTICE */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-8">
          <h2 className="text-red-800 font-bold text-xl mb-2">⚠️ LEGAL REVIEW REQUIRED</h2>
          <p className="text-red-700">
            This is a placeholder cookie policy. A proper policy must be drafted by legal counsel
            before launch, especially considering COPPA requirements for tracking minors.
          </p>
        </div>

        <h1 className="text-4xl font-display font-bold text-rowing-gray-900 mb-8">Cookie Policy</h1>

        <div className="prose prose-lg max-w-none">
          <p className="text-rowing-gray-600 mb-6">
            <strong>Effective Date:</strong> [TO BE DETERMINED]
            <br />
            <strong>Last Updated:</strong> [TO BE DETERMINED]
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-2">Parental Control</h3>
            <p className="text-sm">
              For users under 18, parents can control cookie preferences through the parental
              consent dashboard. Third-party tracking cookies require explicit parental consent.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-rowing-gray-900 mt-8 mb-4">What Are Cookies?</h2>
          <p>
            Cookies are small text files stored on your device when you visit our website. They help
            us provide a better user experience and understand how our platform is used.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Types of Cookies We Use</h3>

          <h4 className="text-lg font-semibold mt-4 mb-2">1. Essential Cookies</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Authentication:</strong> Keep you logged in securely
            </li>
            <li>
              <strong>Security:</strong> Protect against CSRF attacks
            </li>
            <li>
              <strong>Preferences:</strong> Remember your settings
            </li>
            <li>
              <strong>Consent:</strong> Track parental consent status
            </li>
          </ul>

          <h4 className="text-lg font-semibold mt-4 mb-2">
            2. Analytics Cookies (Requires Consent)
          </h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Vercel Analytics:</strong> Anonymous usage statistics
            </li>
            <li>
              <strong>Performance:</strong> Page load times and errors
            </li>
            <li>
              <strong>Navigation:</strong> How users move through the site
            </li>
          </ul>

          <h4 className="text-lg font-semibold mt-4 mb-2">
            3. Third-Party Cookies (Requires Separate Consent)
          </h4>
          <p className="mb-2">With explicit consent, we may use:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Video platforms:</strong> For recruitment videos
            </li>
            <li>
              <strong>Social media:</strong> For sharing features
            </li>
            <li>
              <strong>Analytics services:</strong> For detailed insights
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Cookie Details</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border px-4 py-2 text-left">Cookie Name</th>
                  <th className="border px-4 py-2 text-left">Purpose</th>
                  <th className="border px-4 py-2 text-left">Duration</th>
                  <th className="border px-4 py-2 text-left">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border px-4 py-2">sb-access-token</td>
                  <td className="border px-4 py-2">Authentication</td>
                  <td className="border px-4 py-2">Session</td>
                  <td className="border px-4 py-2">Essential</td>
                </tr>
                <tr>
                  <td className="border px-4 py-2">sb-refresh-token</td>
                  <td className="border px-4 py-2">Session management</td>
                  <td className="border px-4 py-2">30 days</td>
                  <td className="border px-4 py-2">Essential</td>
                </tr>
                <tr>
                  <td className="border px-4 py-2">consent-status</td>
                  <td className="border px-4 py-2">Track consent choices</td>
                  <td className="border px-4 py-2">1 year</td>
                  <td className="border px-4 py-2">Essential</td>
                </tr>
                <tr>
                  <td className="border px-4 py-2">_vercel_analytics</td>
                  <td className="border px-4 py-2">Usage analytics</td>
                  <td className="border px-4 py-2">1 year</td>
                  <td className="border px-4 py-2">Analytics</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-semibold mt-6 mb-3">Managing Cookies</h3>

          <h4 className="text-lg font-semibold mt-4 mb-2">For Users 18+</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Manage preferences in Account Settings</li>
            <li>Use browser settings to block cookies</li>
            <li>Clear cookies at any time</li>
          </ul>

          <h4 className="text-lg font-semibold mt-4 mb-2">For Users Under 18</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Parents control cookie preferences</li>
            <li>Analytics cookies off by default</li>
            <li>Third-party cookies require explicit parental consent</li>
            <li>Parents can withdraw consent anytime</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Browser Cookie Controls</h3>
          <p>You can control cookies through your browser:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Chrome:</strong> Settings → Privacy and security → Cookies
            </li>
            <li>
              <strong>Safari:</strong> Preferences → Privacy
            </li>
            <li>
              <strong>Firefox:</strong> Options → Privacy & Security
            </li>
            <li>
              <strong>Edge:</strong> Settings → Privacy, search, and services
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Impact of Disabling Cookies</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Essential cookies cannot be disabled while using the platform</li>
            <li>Disabling analytics cookies won't affect functionality</li>
            <li>Some features may not work without cookies</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Updates to This Policy</h3>
          <p>
            We may update this policy to reflect changes in our practices or legal requirements.
            We'll notify users and parents of significant changes.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Contact Us</h3>
          <p>
            Questions about our cookie use?
            <br />
            Email: privacy@glyyd.com
            <br />
            See our Privacy Policy for more details
          </p>

          <div className="bg-rowing-gray-100 rounded-lg p-6 mt-8">
            <h3 className="font-semibold mb-2">Legal Counsel Note</h3>
            <p className="text-sm">
              This policy needs to be updated with actual cookie inventory, specific retention
              periods, third-party processor details, international transfer provisions, and
              state-specific requirements. Consider implementing a cookie consent banner with
              granular controls.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
